import { ChangeEvent, FormEvent, useState } from 'react';
import { Button, Link, Stack, Typography } from '@common-components';
import { useBulkCreateInsureds } from 'hooks';
import { messages } from 'i18n';
import { BulkCreateError, Organization } from 'types';
import { Dialog, DialogContent, DialogHeader } from 'components/Dialog';

interface SubmissionsBulkUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization;
}

export default function InsuredsBulkUploadDialog({ isOpen, onClose, organization }: SubmissionsBulkUploadDialogProps) {
  const { bulkCreateInsureds } = useBulkCreateInsureds();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updatedRows, setUpdatedRows] = useState(0);
  const [errors, setErrors] = useState<BulkCreateError[]>([]);

  const { header, title, uploadFileButton, exampleFile, successMessage, errorsTitle, errorMessage } =
    messages.admin.insuredsBulkUploadDialog;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const response = await bulkCreateInsureds.mutateAsync({
      data: {
        file: selectedFile!,
        organizationId: organization.id,
      },
    });
    if (response) {
      setUpdatedRows(response.created.length);
      setErrors(response.errors);
    }
    setIsSubmitting(false);
    setSelectedFile(null);
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    setUpdatedRows(0);
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const closeDialog = () => {
    setIsSubmitting(false);
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeDialog}
      aria-labelledby="insureds-bulk-upload"
      fullScreen
      sx={{ height: 1 }}
      muiPaperProps={{ sx: { bgcolor: 'common.white' } }}
    >
      <DialogHeader title={header} id="insuredsBulkUpload" onClose={closeDialog} />
      <DialogContent sx={{ bgcolor: 'grey.100', height: 1 }}>
        <Typography variant="h5">{title(organization.name)}</Typography>
        <form onSubmit={onSubmit}>
          <Stack mt={3} direction="column" gap={5}>
            <input type="file" onChange={handleFileSelect} />
            <Link
              href="https://docs.google.com/spreadsheets/d/1Uf2vZ0QLWvyjDNTL3GNRcntCxkrW1S96TS064EArmqc"
              target="_blank"
            >
              {exampleFile}
            </Link>
            <Button loading={isSubmitting} onClick={onSubmit} disabled={!selectedFile} variant="contained">
              {uploadFileButton}
            </Button>
          </Stack>
        </form>
        <Stack mt={3} gap={1}>
          <Typography color="green.400" variant="subtitle1">
            {successMessage(updatedRows)}
          </Typography>
          <Typography color="red" variant="subtitle1">{`${errors.length} insureds failed due to errors`}</Typography>
          {errors.length > 0 && (
            <>
              <Typography variant="h5" pt={5}>
                {errorsTitle}
              </Typography>
              <Stack mt={3} gap={1}>
                {errors.map((error) => (
                  <Typography key={error.rowNumber} variant="body2">
                    {errorMessage(error)}
                  </Typography>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
